/**
 * W3 — Detail Workbench Shell domain opt-in (Phase 1 + Phase 2 + Phase 3 bespoke)
 *
 * Phase 1: Finance · Entity · Item (DomainCategory)
 * Phase 2: Calendar · Manufacturing · Knowledge
 * Phase 3 bespoke: per-subdomain allowlist — Flow v2 · Appeal · Finance-Advanced · Space · Organizational
 *
 * Exclusions (shell-off):
 * - Legacy event logs (`flow_event`, `stream_*`)
 * - Site Studio **design canvas** only (`/system/site-studio` with pane=design · select= · default canvas)
 * - Conscious **visualization** bespoke routes (Brain/Neuro/Wisdom/… — partial shell when domain opt-in)
 * - Matrix-only conscious/neuro subdomains (not BusinessDomain)
 *
 * Partial shell (header/breadcrumb only — full Summary/Work tabs break canvas or viz layouts):
 * - Site Studio design canvas
 * - Conscious bespoke visualization routes when domain is opt-in
 *
 * @see doc/system-completion-plan/01-target/BUSINESS_FLOW_WORKBENCH.md §W3
 * @see .cursor/plans/archived/2026-q2/business-flow-workbench/stream-w3-detail-shell.md
 */

import {
  getDomainCategory,
  isBusinessDomain,
  type BusinessDomain,
  type DomainCategory,
} from '@shared/types/business-domain';

/** Phase 1 detail workbench shell — list→detail opt-in domains */
export const DETAIL_WORKBENCH_SHELL_PHASE1_CATEGORIES = [
  'entity',
  'finance',
  'item',
] as const satisfies readonly DomainCategory[];

export type DetailWorkbenchShellPhase1Category =
  (typeof DETAIL_WORKBENCH_SHELL_PHASE1_CATEGORIES)[number];

/** Phase 2 standard domains — policy opt-in only (no global default-on) */
export const DETAIL_WORKBENCH_SHELL_PHASE2_CATEGORIES = [
  'calendar',
  'manufacturing',
  'knowledge',
] as const satisfies readonly DomainCategory[];

export type DetailWorkbenchShellPhase2Category =
  (typeof DETAIL_WORKBENCH_SHELL_PHASE2_CATEGORIES)[number];

/**
 * Phase 3 bespoke categories — documentation only; never category-wide enable.
 * Use {@link DETAIL_WORKBENCH_SHELL_PHASE3_BESPOKE_DOMAINS} for opt-in.
 */
export const DETAIL_WORKBENCH_SHELL_PHASE3_CATEGORIES = [
  'event',
  'appeal',
  'finance_advanced',
  'space',
  'organizational',
] as const satisfies readonly DomainCategory[];

export type DetailWorkbenchShellPhase3Category =
  (typeof DETAIL_WORKBENCH_SHELL_PHASE3_CATEGORIES)[number];

/**
 * Phase 3 bespoke — explicit subdomain shell opt-in (layout-safe list/detail only).
 * Conscious matrix subdomains (consciousness_model, neuro_transmitter, …) are excluded
 * because they are not BusinessDomain.
 */
export const DETAIL_WORKBENCH_SHELL_PHASE3_BESPOKE_DOMAINS = [
  'flow_definition',
  'action_binding',
  'flow_execution',
  'semantic_formation',
  'ephemeral_formation',
  'recurrent_performance',
  'ephemeral_performance',
  'legal_entity',
  'currency_rate',
  'intercompany',
  'physical_space',
  'virtual_space',
  'organizational_state',
] as const satisfies readonly BusinessDomain[];

export type DetailWorkbenchShellPhase3BespokeDomain =
  (typeof DETAIL_WORKBENCH_SHELL_PHASE3_BESPOKE_DOMAINS)[number];

/** Subdomains forced shell-off — legacy logs · layout-risk surfaces */
export const DETAIL_WORKBENCH_SHELL_EXCLUDED_DOMAINS = [
  'flow_event',
  'stream_event',
  'stream_definition',
  'stream_execution',
] as const satisfies readonly BusinessDomain[];

/** Site Studio route prefix — canvas vs list/settings split via query params */
export const DETAIL_WORKBENCH_SHELL_SITE_STUDIO_ROUTE_PREFIX = '/system/site-studio' as const;

/**
 * @deprecated Use {@link DETAIL_WORKBENCH_SHELL_SITE_STUDIO_ROUTE_PREFIX} + canvas query detection.
 * Kept for governance tests that assert the studio route family exists.
 */
export const DETAIL_WORKBENCH_SHELL_EXCLUDED_ROUTE_PREFIXES = [
  DETAIL_WORKBENCH_SHELL_SITE_STUDIO_ROUTE_PREFIX,
] as const;

/**
 * Conscious visualization bespoke pages — full UniversalDetail tabs break layout.
 * Hub · alerts · org-state are **not** listed; domain opt-in applies there.
 */
export const DETAIL_WORKBENCH_SHELL_CONSCIOUS_BESPOKE_ROUTE_PREFIXES = [
  '/conscious/brain',
  '/conscious/neuro',
  '/conscious/wisdom',
  '/conscious/stream',
  '/conscious/pattern',
  '/conscious/memory',
  '/conscious/learning',
  '/conscious/ask',
] as const;

/**
 * @deprecated Use {@link DETAIL_WORKBENCH_SHELL_CONSCIOUS_BESPOKE_ROUTE_PREFIXES}.
 * Kept for importers that referenced the old blanket `/conscious` prefix.
 */
export const DETAIL_WORKBENCH_SHELL_CONSCIOUS_ROUTE_PREFIXES =
  DETAIL_WORKBENCH_SHELL_CONSCIOUS_BESPOKE_ROUTE_PREFIXES;

export type DetailWorkbenchShellVariant = 'full' | 'partial' | 'off';

const OPT_IN_CATEGORY_SET = new Set<string>([
  ...DETAIL_WORKBENCH_SHELL_PHASE1_CATEGORIES,
  ...DETAIL_WORKBENCH_SHELL_PHASE2_CATEGORIES,
]);

const PHASE3_BESPOKE_DOMAIN_SET = new Set<string>(DETAIL_WORKBENCH_SHELL_PHASE3_BESPOKE_DOMAINS);
const EXCLUDED_DOMAIN_SET = new Set<string>(DETAIL_WORKBENCH_SHELL_EXCLUDED_DOMAINS);

const CONSCIOUS_BESPOKE_PREFIXES = DETAIL_WORKBENCH_SHELL_CONSCIOUS_BESPOKE_ROUTE_PREFIXES;

function normalizeRoutePath(routePath: string): string {
  return routePath.split('?')[0] ?? routePath;
}

function parseRouteQuery(routePath: string): URLSearchParams {
  const queryIndex = routePath.indexOf('?');
  if (queryIndex < 0) {
    return new URLSearchParams();
  }
  return new URLSearchParams(routePath.slice(queryIndex + 1));
}

function isSiteStudioRoute(routePath: string): boolean {
  const normalized = normalizeRoutePath(routePath);
  return (
    normalized === DETAIL_WORKBENCH_SHELL_SITE_STUDIO_ROUTE_PREFIX ||
    normalized.startsWith(`${DETAIL_WORKBENCH_SHELL_SITE_STUDIO_ROUTE_PREFIX}/`)
  );
}

/**
 * Site Studio design canvas — 3-pane editor; audience/publish panes are list/settings-safe.
 */
export function isDetailWorkbenchShellSiteStudioCanvasRoute(routePath: string): boolean {
  if (!isSiteStudioRoute(routePath)) {
    return false;
  }
  const query = parseRouteQuery(routePath);
  const pane = query.get('pane');
  if (pane === 'audience' || pane === 'publish') {
    return false;
  }
  if (pane === 'design') {
    return true;
  }
  if (query.has('select')) {
    return true;
  }
  // Bare /system/site-studio is canvas-first (SiteStudioPage).
  return !pane;
}

/** @deprecated Alias — true only for Site Studio design canvas, not audience/publish. */
export function isDetailWorkbenchShellExcludedRoute(routePath: string): boolean {
  return isDetailWorkbenchShellSiteStudioCanvasRoute(routePath);
}

export function isDetailWorkbenchShellConsciousBespokeRoute(routePath: string): boolean {
  const normalized = normalizeRoutePath(routePath);
  return CONSCIOUS_BESPOKE_PREFIXES.some(
    (prefix) => normalized === prefix || normalized.startsWith(`${prefix}/`)
  );
}

/** @deprecated Alias for conscious visualization bespoke routes (not blanket `/conscious`). */
export function isDetailWorkbenchShellConsciousRoute(routePath: string): boolean {
  return isDetailWorkbenchShellConsciousBespokeRoute(routePath);
}

/** Routes that need partial shell (header/breadcrumb only) when domain is opt-in. */
export function isDetailWorkbenchShellPartialRoute(routePath: string): boolean {
  return (
    isDetailWorkbenchShellSiteStudioCanvasRoute(routePath) ||
    isDetailWorkbenchShellConsciousBespokeRoute(routePath)
  );
}

export function isDetailWorkbenchShellExcludedDomain(businessDomain: string): boolean {
  return EXCLUDED_DOMAIN_SET.has(businessDomain);
}

export function isDetailWorkbenchShellPhase3BespokeDomain(businessDomain: string): boolean {
  return PHASE3_BESPOKE_DOMAIN_SET.has(businessDomain);
}

export function isDetailWorkbenchShellOptInDomain(businessDomain: string): boolean {
  if (!isBusinessDomain(businessDomain)) {
    return false;
  }
  if (isDetailWorkbenchShellExcludedDomain(businessDomain)) {
    return false;
  }
  if (isDetailWorkbenchShellPhase3BespokeDomain(businessDomain)) {
    return true;
  }
  return OPT_IN_CATEGORY_SET.has(getDomainCategory(businessDomain as BusinessDomain));
}

/**
 * Resolve shell variant — explicit prop forces full on/off; partial for canvas/viz when domain opt-in.
 */
export function resolveDetailWorkbenchShellVariant(
  businessDomain: string,
  explicit?: boolean,
  routePath?: string
): DetailWorkbenchShellVariant {
  if (explicit === false) {
    return 'off';
  }
  if (explicit === true) {
    return 'full';
  }
  if (!isDetailWorkbenchShellOptInDomain(businessDomain)) {
    return 'off';
  }
  if (routePath && isDetailWorkbenchShellPartialRoute(routePath)) {
    return 'partial';
  }
  return 'full';
}

/**
 * Resolve shell enablement — explicit prop wins; partial counts as enabled.
 */
export function resolveDetailWorkbenchShellEnabled(
  businessDomain: string,
  explicit?: boolean,
  routePath?: string
): boolean {
  return resolveDetailWorkbenchShellVariant(businessDomain, explicit, routePath) !== 'off';
}
